/**
 * Página de Configurações de SLA
 * 
 * @author Equipe GestaoeCRM
 * @date 2025-11-05
 */

import { useState, useEffect } from 'react';
import { 
  Clock, 
  Edit, 
  Save,
  X,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { slaApi, SLAConfiguration, Priority, SLATimeUnit } from '@/services/slaApi';
import toast from 'react-hot-toast';

const PRIORITIES: { value: Priority; label: string; color: string }[] = [
  { value: 'BAIXA', label: 'Baixa', color: 'bg-green-100 text-green-800' },
  { value: 'MEDIA', label: 'Média', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'ALTA', label: 'Alta', color: 'bg-orange-100 text-orange-800' },
  { value: 'CRITICA', label: 'Crítica', color: 'bg-red-100 text-red-800' },
];

export default function SLAPage() {
  const [configurations, setConfigurations] = useState<SLAConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    timeValue: number;
    timeUnit: SLATimeUnit;
  }>({
    timeValue: 24,
    timeUnit: 'HOURS'
  });

  // Carregar configurações
  const loadConfigurations = async () => {
    try {
      setLoading(true);
      const response = await slaApi.getAll();
      const apiPayload = response.data;
      const configs = Array.isArray(apiPayload?.data) ? apiPayload.data : [];

      if (!Array.isArray(apiPayload?.data)) {
        // console.warn('[SLA] Resposta inesperada da API, data não é um array:', apiPayload);
      }

      setConfigurations(configs);
    } catch (error: any) {
      // console.error('Erro ao carregar configurações de SLA:', error);
      toast.error('Erro ao carregar configurações de SLA');
    } finally {
      setLoading(false);
    }
  };

  // Iniciar edição
  const handleEdit = (config: SLAConfiguration) => {
    setEditingId(config.id);
    setFormData({
      timeValue: config.timeValue,
      timeUnit: config.timeUnit
    });
  };

  // Cancelar edição
  const handleCancel = () => {
    setEditingId(null);
    setFormData({
      timeValue: 24,
      timeUnit: 'HOURS'
    });
  };

  // Salvar configuração
  const handleSave = async (priority: Priority) => {
    try {
      if (editingId && editingId !== 'new') {
        // Atualizar existente
        await slaApi.update(editingId, formData);
        toast.success('Configuração de SLA atualizada com sucesso!');
      } else {
        // Criar nova ou atualizar por prioridade
        await slaApi.upsertByPriority(priority, formData);
        toast.success('Configuração de SLA salva com sucesso!');
      }
      
      setEditingId(null);
      await loadConfigurations();
    } catch (error: any) {
      // console.error('Erro ao salvar configuração de SLA:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar configuração de SLA');
    }
  };

  // Buscar configuração por prioridade
  const getConfigByPriority = (priority: Priority): SLAConfiguration | undefined => {
    return configurations.find(c => c.priority === priority);
  };

  // Formatar tempo
  const formatTime = (value: number, unit: SLATimeUnit): string => {
    if (unit === 'HOURS') {
      return value === 1 ? '1 hora' : `${value} horas`;
    } else {
      return value === 1 ? '1 dia' : `${value} dias`;
    }
  };

  useEffect(() => {
    loadConfigurations();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-semibold leading-6 text-text-primary sm:text-2xl sm:truncate">
            Configurações de SLA
          </h2>
          <p className="mt-1 text-sm text-text-muted">
            Configure os prazos de SLA por prioridade para Ordens de Serviço
          </p>
        </div>
      </div>

      {/* Informação */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-1">Como funciona o SLA:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>O SLA é calculado a partir da última execução do timer (workSession)</li>
              <li>Se a OS nunca foi executada, o cálculo usa a data de criação</li>
              <li>OS que estiverem fora do SLA aparecerão em vermelho nas páginas de OS e Projetos</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Tabela de Configurações */}
      <div className="bg-background-glass backdrop-blur-glass shadow rounded-glass overflow-hidden">
        <table className="min-w-full divide-y divide-border-glass">
          <thead className="bg-background-glass-hover">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-primary uppercase tracking-wider">
                Prioridade
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-primary uppercase tracking-wider">
                Tempo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-primary uppercase tracking-wider">
                Unidade
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-text-primary uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-background-glass divide-y divide-border-glass">
            {PRIORITIES.map((priority) => {
              const config = getConfigByPriority(priority.value);
              const isEditing = editingId === config?.id || editingId === 'new-' + priority.value;

              return (
                <tr key={priority.value} className="hover:bg-background-glass-hover">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priority.color}`}>
                      {priority.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                    {isEditing ? (
                      <input
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={formData.timeValue}
                        onChange={(e) => setFormData({ ...formData, timeValue: parseFloat(e.target.value) || 0 })}
                        className="w-24 px-2 py-1 border border-border-glass rounded-button shadow-glass bg-background-glass text-text-primary focus:ring-secondary focus:border-secondary/50"
                      />
                    ) : (
                      config ? formatTime(config.timeValue, config.timeUnit) : '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                    {isEditing ? (
                      <select
                        value={formData.timeUnit}
                        onChange={(e) => setFormData({ ...formData, timeUnit: e.target.value as SLATimeUnit })}
                        className="px-2 py-1 border border-border-glass rounded-button shadow-glass bg-background-glass text-text-primary focus:ring-secondary focus:border-secondary/50"
                      >
                        <option value="HOURS">Horas</option>
                        <option value="DAYS">Dias</option>
                      </select>
                    ) : (
                      config ? (config.timeUnit === 'HOURS' ? 'Horas' : 'Dias') : '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {isEditing ? (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleSave(priority.value)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-button text-xs font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <Save className="h-3 w-3 mr-1" />
                          Salvar
                        </button>
                        <button
                          onClick={handleCancel}
                          className="inline-flex items-center px-3 py-1.5 border border-border-glass rounded-button text-xs font-medium text-text-primary bg-background-glass hover:bg-background-glass-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                        >
                          <X className="h-3 w-3 mr-1" />
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          if (config) {
                            handleEdit(config);
                          } else {
                            setEditingId('new-' + priority.value);
                            setFormData({
                              timeValue: 24,
                              timeUnit: 'HOURS'
                            });
                          }
                        }}
                        className="inline-flex items-center px-3 py-1.5 border border-border-glass rounded-button text-xs font-medium text-text-primary bg-background-glass hover:bg-background-glass-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                      >
                        {config ? (
                          <>
                            <Edit className="h-3 w-3 mr-1" />
                            Editar
                          </>
                        ) : (
                          <>
                            <Clock className="h-3 w-3 mr-1" />
                            Configurar
                          </>
                        )}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Status */}
      {configurations.length > 0 && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-3" />
            <div className="text-sm text-green-800 dark:text-green-200">
              <span className="font-medium">{configurations.length} de {PRIORITIES.length} prioridades configuradas</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

